// v2 reeds — a true fan: flat blades rising from one rootstock and spreading out
// across a plane, the inner blades tall and near-vertical, the outer ones shorter
// and arching away, two of the tall blades tipped with a slim brown seed head.
// Variants turn the fan and dry the green. Ported 1:1 from age-of-mcp's
// v2/reeds.ts, using the library's shared `groundcover` (bladeArc, dirAt,
// tiltRot, h01, lerp) helpers.
import * as THREE from "three";
import { defineModel, groundcover, type Part } from "../../lib";

const TONES: [string, string, string][] = [
  ["#5f8a3e", "#6f9a46", "#80ad52"],   // fresh marsh green
  ["#7d9447", "#8da450", "#9eb45a"],   // drier, yellowed stand
];

function reedsParts(v: number): Part[] {
  const seed = v * 17 + 5;
  const yaw = v * 2.3 + 0.8;
  const cols = TONES[v % TONES.length]!;
  const parts: Part[] = [];
  const N = 7;
  const headed: { tip: [number, number, number]; tilt: number; ang: number }[] = [];
  for (let i = 0; i < N; i++) {
    const u = i / (N - 1) - 0.5;                  // -0.5 .. 0.5 across the fan
    const a = yaw + u * 1.5 + (i % 2 ? 0.18 : -0.18) + groundcover.h01(seed, i) * 0.2;
    const lean = 0.05 + Math.abs(u) * 0.42;       // outer blades lean away
    const len = groundcover.lerp(0.7, 0.46, Math.abs(u) * 2); // inner blades tallest
    const arc = groundcover.bladeArc({
      x: Math.cos(a) * 0.035, z: Math.sin(a) * 0.035, ang: a,
      lean, arch: groundcover.lerp(0.22, 0.4, groundcover.h01(seed, i + 8)),
      len, w: 0.02, t: 0.005, color: cols[i % 3]!,
    });
    parts.push(...arc.parts);
    if (i === 1 || i === 3) headed.push({ tip: arc.tip, tilt: arc.tipTilt, ang: a });
  }
  // slim seed heads on two of the tall blades
  for (const h of headed) {
    const d = groundcover.dirAt(h.ang, h.tilt);
    parts.push({
      geometry: new THREE.CylinderGeometry(0.011, 0.007, 0.075, 5),
      color: "#8a653c",
      position: [h.tip[0] + d[0] * 0.03, h.tip[1] + d[1] * 0.03, h.tip[2] + d[2] * 0.03],
      rotation: groundcover.tiltRot(h.ang, h.tilt),
    });
  }
  return parts;
}

export const reedsV2 = defineModel({
  id: "reeds-v2",
  batchId: "fable",
  name: "Reeds (v2)",
  tags: ["scenery","reeds","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly reeds.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.3,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => reedsParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => reedsParts(1) },
  ],
  animations: [],
});
